import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { isVersionConflict } from "../lib/api";
import { formatDateTime, missionErrorMessage } from "../lib/missions";
import {
  VERSION_STATUS_LABELS, draftProblem, moveItem, moveSection, newItem, newSection, removeItem, toPayload, versionSummary,
} from "../lib/procedures";
import useIsMobile from "../hooks/useIsMobile";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { ItemEditor, ProcedureOutline, ProcedurePreview } from "../components/procedures/ProcedureEditorBits";
import { StatePill } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, Eye, ListTree, Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useBottomBar } from "../hooks/useCopilotDock";

// One editor for two versioned templates with the same cycle and rules: checklists (procedures) and mission report templates (Ordre 15).
const EDITOR_KINDS = {
  procedure: {
    api: "/procedures", back: "/missions/procedures", backLabel: "Retour aux procédures", errorCode: "invalid_procedure", firstKind: "status",
    checklist: true, noun: "Procédure", missing: "Cette procédure n'existe pas ou vous n'y avez pas accès.", loadError: "Impossible de charger la procédure.",
    firstSection: "Commencez par une section, par exemple « Préparation » ou « Caisse ».",
    publishHelp: "Les procédures démarrées à partir de maintenant utiliseront cette version. Celles déjà commencées gardent la leur. Une version publiée ne se modifie plus.",
    published: "utilisée par les nouvelles missions", deprecated: "gardée pour les missions déjà faites",
    deactivate: "Elle ne sera plus proposée pour les types de mission ni démarrée dans de nouvelles missions. Ce qui a déjà été fait reste consultable.",
    off: "Procédure désactivée", on: "Procédure réactivée", updated: "Procédure mise à jour", reactivate: "Elle pourra de nouveau être utilisée.",
    created: "Procédure créée", modified: "Procédure modifiée",
  },
  report: {
    api: "/report-templates", back: "/collaborateurs/organisation?tab=report_templates", backLabel: "Retour aux modèles de compte rendu", errorCode: "invalid_template",
    firstKind: "text", checklist: false, textMax: 5000, noun: "Modèle", missing: "Ce modèle n'existe pas ou vous n'y avez pas accès.",
    loadError: "Impossible de charger le modèle.", firstSection: "Commencez par une section, par exemple « Installation » ou « Bilan ».",
    publishHelp: "Les comptes rendus commencés à partir de maintenant utiliseront cette version. Ceux déjà commencés gardent la leur. Une version publiée ne se modifie plus.",
    published: "utilisée par les nouveaux comptes rendus", deprecated: "gardée pour les comptes rendus déjà commencés",
    deactivate: "Il ne sera plus proposé pour les types de mission : ceux qui l'utilisent reviennent au modèle standard. Les comptes rendus déjà écrits restent consultables.",
    off: "Modèle désactivé", on: "Modèle réactivé", updated: "Modèle mis à jour", reactivate: "Il pourra de nouveau être utilisé.",
    created: "Modèle créé", modified: "Modèle modifié",
  },
};

const HISTORY_LABELS = {
  draft_created: "Nouvelle version commencée", draft_saved: "Brouillon enregistré", draft_discarded: "Brouillon supprimé", published: "Version publiée",
};

export default function ProcedureEditor({ kind = "procedure" }) {
  const cfg = EDITOR_KINDS[kind];
  const { id } = useParams();
  const mobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(null);
  const [template, setTemplate] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | missing | error
  const [errorText, setErrorText] = useState("");
  const [versionId, setVersionId] = useState(null);
  const [version, setVersion] = useState(null);
  const [sections, setSections] = useState([]);
  const [saved, setSaved] = useState("[]");
  const [openItem, setOpenItem] = useState(null);
  const [view, setView] = useState("edit"); // edit | preview (phone)
  const [busy, setBusy] = useState(false);
  const [serverProblem, setServerProblem] = useState(null);
  const [dialog, setDialog] = useState(null); // publish | discard | details | toggle

  const loadTemplate = useCallback(async (preferred) => {
    try {
      const { data } = await api.get(`${cfg.api}/${id}`);
      setTemplate(data);
      setPhase("ready");
      const next = preferred || data.draft_version?.id || data.current_version?.id || data.versions[0]?.id || null;
      setVersionId(next);
      return data;
    } catch (e) {
      if (e?.response?.status === 404) setPhase("missing");
      else { setErrorText(missionErrorMessage(e, cfg.loadError)); setPhase("error"); }
      return null;
    }
  }, [id, cfg]);
  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const loadVersion = useCallback(async (vid) => {
    if (!vid) { setVersion(null); setSections([]); setSaved("[]"); return; }
    try {
      const { data } = await api.get(`${cfg.api}/${id}/versions/${vid}`);
      setVersion(data);
      setSections(data.sections);
      setSaved(JSON.stringify(data.sections));
      setServerProblem(null);
    } catch (e) {
      toast.error(missionErrorMessage(e, "Impossible de charger cette version."));
    }
  }, [id, cfg]);
  useEffect(() => { loadVersion(versionId); }, [versionId, loadVersion]);

  const isDraft = version?.status === "draft";
  const dirty = isDraft && JSON.stringify(sections) !== saved;
  useBottomBar(dirty);
  const problem = useMemo(() => (isDraft ? draftProblem(sections) : null), [isDraft, sections]);
  const publishProblem = useMemo(() => (isDraft ? draftProblem(sections, { publishing: true }) : null), [isDraft, sections]);
  const shownProblem = problem || serverProblem;

  const updateItem = (sectionId, item) => setSections((all) => all.map((s) => (s.id === sectionId ? { ...s, items: s.items.map((i) => (i.id === item.id ? item : i)) } : s)));
  const addItem = (sectionId) => {
    const item = newItem(cfg.firstKind);
    setSections((all) => all.map((s) => (s.id === sectionId ? { ...s, items: [...s.items, item] } : s)));
    setOpenItem(item.id);
  };

  const run = async (request, success) => {
    setBusy(true);
    try {
      const { data } = await request();
      toast.success(success);
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === cfg.errorCode) {
        setServerProblem({ message: detail.msg, path: detail.path });
        if (detail.path?.includes("/")) setOpenItem(detail.path.split("/")[1]);
      }
      toast.error(missionErrorMessage(e));
      if (isVersionConflict(e)) { await loadTemplate(versionId); await loadVersion(versionId); }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const data = await run(() => api.put(`${cfg.api}/${id}/versions/${version.id}`, { expected_version: version.version, sections: toPayload(sections) }),
      "Brouillon enregistré");
    if (data) { setVersion(data); setSections(data.sections); setSaved(JSON.stringify(data.sections)); setServerProblem(null); }
  };
  const publish = async () => {
    const data = await run(() => api.post(`${cfg.api}/${id}/versions/${version.id}/publish`, { expected_version: version.version }),
      `Version ${version.number} publiée`);
    // Same version id before and after: show the published version the server returned (the effect on versionId would not re-run).
    if (data) { setDialog(null); setVersion(data); setSections(data.sections); setSaved(JSON.stringify(data.sections)); await loadTemplate(data.id); }
  };
  const startVersion = async () => {
    const data = await run(() => api.post(`${cfg.api}/${id}/versions`), "Nouvelle version commencée");
    if (data) await loadTemplate(data.id);
  };
  const discard = async () => {
    const data = await run(() => api.post(`${cfg.api}/${id}/versions/${version.id}/discard`, { expected_version: version.version }), "Brouillon supprimé");
    if (data) { setDialog(null); await loadTemplate(data.current_version?.id || null); }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="procedure-missing">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? cfg.missing : errorText}</p>
        <Button variant="outline" asChild><Link to={cfg.back}>{cfg.backLabel}</Link></Button>
      </Card>
    );
  }

  const editor = isDraft && (
    <div className="space-y-4" data-testid="procedure-editor">
      {sections.length === 0 && (
        <Card className="p-6 text-center text-sm text-slate-500">{cfg.firstSection}</Card>
      )}
      {sections.map((section, sIndex) => (
        <Card key={section.id} className={`p-4 space-y-3 ${shownProblem?.path === section.id ? "border-amber-400" : ""}`} data-testid={`section-editor-${sIndex}`}>
          <div className="flex items-center gap-2">
            <Input className="text-base md:text-sm font-medium" maxLength={200} placeholder="Titre de la section" value={section.title} aria-label="Titre de la section"
              onChange={(e) => setSections((all) => all.map((s) => (s.id === section.id ? { ...s, title: e.target.value } : s)))} data-testid={`section-title-${sIndex}`} />
            <Button type="button" variant="ghost" size="icon" aria-label="Monter la section" disabled={sIndex === 0} onClick={() => setSections((all) => moveSection(all, section.id, -1))}><ArrowUp size={14} /></Button>
            <Button type="button" variant="ghost" size="icon" aria-label="Descendre la section" disabled={sIndex === sections.length - 1} onClick={() => setSections((all) => moveSection(all, section.id, 1))}><ArrowDown size={14} /></Button>
            <Button type="button" variant="ghost" size="icon" aria-label="Retirer la section" className="text-slate-500 hover:text-red-700"
              onClick={() => setSections((all) => section.items.reduce((acc, item) => removeItem(acc, item.id), all).filter((s) => s.id !== section.id))}><Trash2 size={14} /></Button>
          </div>
          {shownProblem?.path === section.id && <p className="text-xs text-amber-800">{shownProblem.message}</p>}
          <div className="space-y-2">
            {section.items.map((item, iIndex) => (
              <ItemEditor key={item.id} item={item} sections={sections} open={openItem === item.id} onToggle={() => setOpenItem(openItem === item.id ? null : item.id)}
                onChange={(next) => updateItem(section.id, next)} onRemove={() => setSections((all) => removeItem(all, item.id))}
                onMove={(delta) => setSections((all) => moveItem(all, item.id, delta))} canMoveUp={iIndex > 0} canMoveDown={iIndex < section.items.length - 1}
                problem={shownProblem?.path === `${section.id}/${item.id}` ? shownProblem.message : null} checklist={cfg.checklist} />
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => addItem(section.id)} data-testid={`section-add-item-${sIndex}`}><Plus size={14} className="mr-1.5" />Ajouter un élément</Button>
        </Card>
      ))}
      <Button type="button" variant="outline" onClick={() => setSections((all) => [...all, newSection("")])} data-testid="procedure-add-section">
        <Plus size={16} className="mr-1.5" />Ajouter une section
      </Button>
    </div>
  );

  return (
    <div className="space-y-4 pb-24" data-testid="procedure-page">
      <DeleteWithReasonDialog
        target={deleting} testId="delete-procedure" onClose={() => setDeleting(null)}
        title={`Supprimer « ${deleting?.label || ""} » ?`}
        description={`${kind === "report" ? "Ce modèle" : "Cette procédure"} n'a jamais été publiée ni utilisée. Elle va dans la corbeille : vous pourrez la restaurer pendant 30 jours.`}
        onConfirm={async (reason) => {
          try {
            await api.delete(`${cfg.api}/${id}`, { data: { reason } });
            toast.success(`${cfg.noun} mis à la corbeille`);
            navigate(cfg.back);
          } catch (e) {
            toast.error(missionErrorMessage(e, "Impossible de supprimer."));
          } finally {
            setDeleting(null);
          }
        }}
      />
      <Link to={cfg.back} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> {cfg.backLabel}</Link>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="procedure-title">{template.label}</h1>
            <StatePill active={template.active} activeLabel={kind === "report" ? "Actif" : "Active"} inactiveLabel={kind === "report" ? "Désactivé" : "Désactivée"} />
          </div>
          {template.description && <p className="text-sm text-slate-500">{template.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isDraft && (
            <Button onClick={() => setDialog("publish")} disabled={busy || dirty}  data-testid="procedure-publish"
              title={dirty ? "Enregistrez d'abord vos modifications" : undefined}>Publier la version {version.number}</Button>
          )}
          {!template.draft_version && <Button variant="outline" onClick={startVersion} disabled={busy} data-testid="procedure-new-version">Nouvelle version</Button>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="procedure-more"><MoreHorizontal size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" title={cfg.noun}>
              <DropdownMenuItem onClick={() => setDialog("details")} data-testid="procedure-edit-details"><Pencil size={14} className="mr-2" />Nom et description</DropdownMenuItem>
              {!template.standard && <DropdownMenuItem onClick={() => setDialog("toggle")} data-testid="procedure-toggle">{template.active ? "Désactiver" : "Réactiver"}</DropdownMenuItem>}
              {isDraft && <DropdownMenuItem onClick={() => setDialog("discard")} className="text-red-700" data-testid="procedure-discard">Supprimer le brouillon</DropdownMenuItem>}
              {!template.standard && hasPermission(user, "missions:delete") && (
                <DropdownMenuItem onClick={() => setDeleting(template)} className="text-red-700" data-testid="procedure-delete">Supprimer {kind === "report" ? "le modèle" : "la procédure"}…</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Versions" data-testid="procedure-versions">
        {template.versions.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={v.id === versionId} onClick={() => !dirty && setVersionId(v.id)} disabled={dirty && v.id !== versionId}
            data-testid={`procedure-version-${v.number}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${v.id === versionId ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            v{v.number} · {VERSION_STATUS_LABELS[v.status]}
          </button>
        ))}
      </div>

      {!version ? (
        <Card className="p-6 text-sm text-slate-500">Aucune version. Commencez-en une avec « Nouvelle version ».</Card>
      ) : (
        <>
          <p className="text-sm text-slate-500" data-testid="procedure-version-summary">
            {isDraft ? "Brouillon, modifiable" : version.status === "published" ? `Publiée le ${formatDateTime(version.published_at)}, ${cfg.published}` : `Remplacée le ${formatDateTime(version.deprecated_at)}, ${cfg.deprecated}`}
            {" · "}{versionSummary(version)}
          </p>
          {mobile && (
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist">
              {[["edit", isDraft ? "Modifier" : "Structure", isDraft ? Pencil : ListTree], ["preview", "Aperçu", Eye]].map(([key, label, Icon]) => (
                <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setView(key)} data-testid={`procedure-view-${key}`}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${view === key ? "bg-jamora text-white" : "text-slate-600"}`}><Icon size={14} />{label}</button>
              ))}
            </div>
          )}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_400px] items-start">
            {(!mobile || view === "edit") && (isDraft ? editor : <ProcedureOutline sections={sections} checklist={cfg.checklist} />)}
            {(!mobile || view === "preview") && <div className="lg:sticky lg:top-24"><ProcedurePreview sections={sections} textMax={cfg.textMax} /></div>}
          </div>
        </>
      )}

      {template.history?.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-white p-4" data-testid="procedure-history">
          <summary className="cursor-pointer text-sm font-medium text-slate-900">Historique</summary>
          <ol className="mt-3 space-y-2">
            {template.history.map((h, i) => (
              <li key={i} className="text-sm">
                <span className="text-slate-800">{{ created: cfg.created, updated: cfg.modified }[h.kind] || HISTORY_LABELS[h.kind] || h.kind}{h.data?.number ? ` (v${h.data.number})` : ""}</span>
                <span className="text-xs text-slate-500"> · {formatDateTime(h.at)} · {h.actor_name}</span>
              </li>
            ))}
          </ol>
        </details>
      )}

      {dirty && (
        <div className="fixed inset-x-0 bottom-[4.75rem] lg:bottom-4 z-30 px-4 lg:left-64" data-testid="procedure-save-bar">
          <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-3 shadow-lg flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500 min-w-0">{problem ? <span className="text-amber-800">{problem.message}</span> : "Modifications non enregistrées"}</span>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => { setSections(JSON.parse(saved)); setServerProblem(null); }}>Annuler</Button>
              <Button size="sm" onClick={save} disabled={busy || !!problem}  data-testid="procedure-save">
                {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={dialog === "publish"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent data-testid="procedure-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publier la version {version?.number} ?</DialogTitle>
            <DialogDescription>
              {cfg.publishHelp}
            </DialogDescription>
          </DialogHeader>
          {publishProblem && <p className="text-sm text-amber-800" role="alert">{publishProblem.message}</p>}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Annuler</Button>
            <Button onClick={publish} disabled={busy || !!publishProblem}  data-testid="procedure-publish-confirm">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Publier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "discard"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le brouillon v{version?.number} ?</DialogTitle>
            <DialogDescription>Il n'a jamais été publié : rien d'autre ne change. Les versions publiées restent.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Annuler</Button>
            <Button onClick={discard} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="procedure-discard-confirm">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DetailsDialog cfg={cfg} open={dialog === "details" || dialog === "toggle"} mode={dialog} template={template} onClose={() => setDialog(null)}
        onDone={async (data) => { setDialog(null); setTemplate((t) => ({ ...t, ...data })); }} />
    </div>
  );
}

function DetailsDialog({ cfg, open, mode, template, onClose, onDone }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLabel(template.label); setDescription(template.description || ""); } }, [open, template]);
  const toggle = mode === "toggle";
  const submit = async () => {
    setBusy(true);
    try {
      const body = toggle ? { expected_version: template.version, active: !template.active }
        : { expected_version: template.version, label: label.trim(), description: description.trim() || null };
      const { data } = await api.patch(`${cfg.api}/${template.id}`, body);
      toast.success(toggle ? (template.active ? cfg.off : cfg.on) : cfg.updated);
      onDone(data);
    } catch (e) {
      toast.error(missionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="procedure-details-dialog">
        <DialogHeader>
          <DialogTitle>{toggle ? (template.active ? `Désactiver « ${template.label} » ?` : `Réactiver « ${template.label} » ?`) : "Nom et description"}</DialogTitle>
          {toggle && (
            <DialogDescription>
              {template.active ? cfg.deactivate : cfg.reactivate}
            </DialogDescription>
          )}
        </DialogHeader>
        {!toggle && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="pe-label">Nom *</Label>
              <Input id="pe-label" maxLength={120} className="text-base md:text-sm" value={label} onChange={(e) => setLabel(e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="pe-desc">Description</Label>
              <Textarea id="pe-desc" rows={2} maxLength={2000} className="text-base md:text-sm" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || (!toggle && !label.trim())}  data-testid="procedure-details-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{toggle ? (template.active ? "Désactiver" : "Réactiver") : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
